import { useMemo } from 'react';
import './DocumentsTable.css';
import { RecordCard, RecordCardList } from '../primitives';
import {
  Table,
  type BulkAction,
  type CellValue,
  type Column,
  type Row as TableRow,
  type SortDescriptor,
  type TableUrlStateAdapter,
  type UrlState
} from '../Table';
import type { DocumentRow } from '../../lib/models/documents';
import type {
  DocumentSystemRowValues,
  DocumentTypeOption,
  DocumentMemberOption
} from '../../lib/vm/useDocumentsListVM';
import { UNTITLED_DOCUMENT_TITLE } from '../../lib/editor/defaultTitles';
import { formatRelativeTime } from '../../lib/relativeTime';

const LIFECYCLE_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' }
];

export const TABLE_DEFAULT_SORT: SortDescriptor = {
  columnKey: 'last_event_at',
  direction: 'desc'
};

export const TABLE_COLUMN_KEYS = [
  'title',
  'type',
  'lifecycle',
  'owner',
  'last_event_at',
  'created_at'
] as const;

export type TableColumnKey = (typeof TABLE_COLUMN_KEYS)[number];

export const TABLE_COLUMN_LABELS: Readonly<Record<TableColumnKey, string>> = {
  title: 'Title',
  type: 'Type',
  lifecycle: 'Lifecycle',
  owner: 'Owner',
  last_event_at: 'Edited',
  created_at: 'Created'
};

export const TABLE_DEFAULT_VISIBLE: ReadonlyArray<TableColumnKey> = [
  'title',
  'type',
  'lifecycle',
  'owner',
  'last_event_at'
];

export const TABLE_LOCKED_COLUMNS: ReadonlyArray<string> = ['title'];

const LIFECYCLE_LABEL_BY_VALUE: Readonly<Record<string, string>> =
  Object.fromEntries(LIFECYCLE_OPTIONS.map((o) => [o.value, o.label]));

export interface DocumentCardLookups {
  type: ReadonlyMap<string, string>;
  owner: ReadonlyMap<string, string>;
}

function documentCardChipValue(
  key: string,
  row: DocumentRow,
  sys: DocumentSystemRowValues | undefined,
  lookups: DocumentCardLookups
): string | null {
  switch (key) {
    case 'type':
      return row.type_id ? lookups.type.get(row.type_id) ?? null : null;
    case 'lifecycle':
      return sys?.lifecycle
        ? LIFECYCLE_LABEL_BY_VALUE[sys.lifecycle] ?? sys.lifecycle
        : null;
    case 'owner':
      return sys?.ownerId ? lookups.owner.get(sys.ownerId) ?? null : null;
    case 'last_event_at':
      return row.last_event_at ? formatRelativeTime(row.last_event_at) : null;
    case 'created_at':
      return row.created_at ? formatRelativeTime(row.created_at) : null;
    default:
      return null;
  }
}

function buildTableUrlAdapter(
  currentSort: SortDescriptor,
  onSortChange: (next: SortDescriptor) => void,
  visibleCols: ReadonlyArray<string>,
  onColsChange: (next: ReadonlyArray<string>) => void
): TableUrlStateAdapter {
  return {
    read(): UrlState {
      return { sort: currentSort, cols: [...visibleCols] };
    },
    write(s: UrlState) {
      if (
        s.sort &&
        (s.sort.columnKey !== currentSort.columnKey ||
          s.sort.direction !== currentSort.direction)
      ) {
        onSortChange(s.sort);
      }
      if (s.cols && s.cols.length > 0) {
        const sameLen = s.cols.length === visibleCols.length;
        const sameOrder =
          sameLen && s.cols.every((k, i) => k === visibleCols[i]);
        if (!sameOrder) onColsChange(s.cols);
      }
    }
  };
}

function DocumentPropertyCard({
  row,
  sys,
  chipKeys,
  lookups,
  onOpen,
  dataTestId
}: {
  row: DocumentRow;
  sys: DocumentSystemRowValues | undefined;
  chipKeys: ReadonlyArray<string>;
  lookups: DocumentCardLookups;
  onOpen: () => void;
  dataTestId: string;
}) {
  const chips = chipKeys
    .map((k) => ({ key: k, value: documentCardChipValue(k, row, sys, lookups) }))
    .filter((c) => c.value != null);

  return (
    <RecordCard
      layout="stack"
      interactive
      onActivate={onOpen}
      data-testid={`${dataTestId}-table-card-${row.id}`}
      data-card-id={row.id}
    >
      <RecordCard.Title>{row.title || UNTITLED_DOCUMENT_TITLE}</RecordCard.Title>
      {chips.length > 0 ? (
        <RecordCard.Meta data-testid={`${dataTestId}-table-card-${row.id}-chips`}>
          {chips.map((chip) => (
            <RecordCard.Chip
              key={chip.key}
              className={`pn-record-card-chip--${chip.key}`}
              data-testid={`${dataTestId}-table-card-${row.id}-chip-${chip.key}`}
              data-chip-key={chip.key}
            >
              {`${TABLE_COLUMN_LABELS[chip.key as TableColumnKey] ?? chip.key}: ${chip.value}`}
            </RecordCard.Chip>
          ))}
        </RecordCard.Meta>
      ) : null}
    </RecordCard>
  );
}

export interface DocumentsTableProps {
  rows: ReadonlyArray<DocumentRow>;
  systemValuesByDoc: ReadonlyMap<string, DocumentSystemRowValues>;
  typeOptions: ReadonlyArray<DocumentTypeOption>;
  memberOptions: ReadonlyArray<DocumentMemberOption>;
  typeLookup: ReadonlyMap<string, string>;
  ownerLookup: ReadonlyMap<string, string>;
  visibleProperties: ReadonlyArray<string>;
  sort: SortDescriptor;
  onSortChange: (next: SortDescriptor) => void;
  onColsChange: (next: ReadonlyArray<string>) => void;
  onRowOpen: (id: string) => void;
  onCellEdit: (rowId: string, columnKey: string, newValue: CellValue) => Promise<void>;
  onBulkAction: (rowIds: string[], action: BulkAction) => Promise<void>;
  /** Test-id prefix for the Table primitive (e.g. 'documents' or 'project-documents'). */
  dataTestId: string;
}

export function DocumentsTable({
  rows,
  systemValuesByDoc,
  typeOptions,
  memberOptions,
  typeLookup,
  ownerLookup,
  visibleProperties,
  sort,
  onSortChange,
  onColsChange,
  onRowOpen,
  onCellEdit,
  onBulkAction,
  dataTestId
}: DocumentsTableProps) {
  const tableColumns = useMemo<Column[]>(() => {
    const typeOpts = typeOptions
      .filter((t) => t.key !== 'canvas')
      .map((t) => ({ value: t.id, label: t.name }));
    const memberOpts = memberOptions.map((m) => ({
      value: m.userId,
      label: m.displayName
    }));
    const all: Record<TableColumnKey, Column> = {
      title: { key: 'title', label: 'Title', type: 'text', editable: false },
      type: { key: 'type', label: 'Type', type: 'single_select', options: typeOpts, editable: true },
      lifecycle: { key: 'lifecycle', label: 'Lifecycle', type: 'single_select', options: LIFECYCLE_OPTIONS, editable: true },
      owner: { key: 'owner', label: 'Owner', type: 'person', options: memberOpts, editable: true },
      last_event_at: { key: 'last_event_at', label: 'Edited', type: 'text', editable: false },
      created_at: { key: 'created_at', label: 'Created', type: 'text', editable: false }
    };
    return TABLE_COLUMN_KEYS.map((k) => all[k]);
  }, [typeOptions, memberOptions]);

  const tableRows = useMemo<TableRow[]>(
    () =>
      rows.map((r) => {
        const sys = systemValuesByDoc.get(r.id);
        return {
          id: r.id,
          title: r.title,
          type: r.type_id ?? '',
          lifecycle: sys?.lifecycle ?? '',
          owner: sys?.ownerId ?? '',
          last_event_at: formatRelativeTime(r.last_event_at),
          created_at: formatRelativeTime(r.created_at)
        };
      }),
    [rows, systemValuesByDoc]
  );

  const cardChipKeys = useMemo(
    () => TABLE_COLUMN_KEYS.filter((k) => k !== 'title' && visibleProperties.includes(k)),
    [visibleProperties]
  );

  const cardLookups = useMemo<DocumentCardLookups>(
    () => ({ type: typeLookup, owner: ownerLookup }),
    [typeLookup, ownerLookup]
  );

  const urlAdapter = useMemo(
    () => buildTableUrlAdapter(sort, onSortChange, visibleProperties, onColsChange),
    [sort, onSortChange, visibleProperties, onColsChange]
  );

  return (
    <div className="doc-table-wrap">
      <Table
        columns={tableColumns}
        rows={tableRows}
        defaultSort={TABLE_DEFAULT_SORT}
        onCellEdit={onCellEdit}
        onBulkAction={onBulkAction}
        multiSelectEnabled
        urlState={urlAdapter}
        dataTestId={dataTestId}
        onRowOpen={onRowOpen}
      />
      <RecordCardList
        className="doc-table-cards"
        data-testid={`${dataTestId}-table-cards`}
        data-visible-chips={cardChipKeys.join(',')}
      >
        {rows.map((r) => (
          <DocumentPropertyCard
            key={r.id}
            row={r}
            sys={systemValuesByDoc.get(r.id)}
            chipKeys={cardChipKeys}
            lookups={cardLookups}
            onOpen={() => onRowOpen(r.id)}
            dataTestId={dataTestId}
          />
        ))}
      </RecordCardList>
    </div>
  );
}
